import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Check, RefreshCcw, X } from "lucide-react";

export default function Camara({ titulo, onCaptura, onCancelar, soloPrueba = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cuenta, setCuenta] = useState(3);
  const [foto, setFoto] = useState(null);
  const [sinCamara, setSinCamara] = useState(false);

  useEffect(() => {
    let activo = true;
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } })
        .then((s) => {
          if (!activo) { s.getTracks().forEach((t) => t.stop()); return; }
          streamRef.current = s;
          if (videoRef.current) videoRef.current.srcObject = s;
        })
        .catch(() => setSinCamara(true));
    } else {
      setSinCamara(true);
    }
    return () => { activo = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  useEffect(() => {
    if (foto) return;
    if (cuenta <= 0) { capturar(); return; }
    const t = setTimeout(() => setCuenta((c) => c - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuenta, foto, sinCamara]);

  const capturar = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext("2d");
    if (!sinCamara && videoRef.current && videoRef.current.readyState >= 2 && videoRef.current.videoWidth > 0) {
      ctx.drawImage(videoRef.current, 0, 0, 640, 480);
    } else {
      // Foto simulada: placeholder con fecha y hora impresas, para que la demo no se trabe
      ctx.fillStyle = "#151A21";
      ctx.fillRect(0, 0, 640, 480);
      ctx.strokeStyle = "#2DD4BF";
      ctx.lineWidth = 6;
      ctx.strokeRect(16, 16, 608, 448);
      ctx.textAlign = "center";
      ctx.fillStyle = "#2DD4BF";
      ctx.font = "bold 34px sans-serif";
      ctx.fillText("FOTO SIMULADA", 320, 200);
      ctx.fillStyle = "#e5e7eb";
      ctx.font = "26px sans-serif";
      const txt = new Intl.DateTimeFormat("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires", day: "2-digit", month: "2-digit",
        year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }).format(new Date());
      ctx.fillText(txt, 320, 260);
      ctx.fillStyle = "#6b7280";
      ctx.font = "20px sans-serif";
      ctx.fillText("Cámara no disponible en este equipo", 320, 312);
    }
    setFoto(canvas.toDataURL("image/jpeg", 0.8));
  };

  const repetir = () => { setFoto(null); setCuenta(3); };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B0F14]/95 p-6 backdrop-blur"
      data-testid="camara-overlay"
    >
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#151A21] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-bold text-white">
            <Camera size={20} className="text-[#2DD4BF]" /> {titulo}
          </h3>
          <button
            onClick={onCancelar}
            className="rounded-xl border border-white/10 p-2 text-gray-400 transition-colors hover:border-red-400/50 hover:text-red-300"
            data-testid="camara-cancelar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black" style={{ aspectRatio: "4/3" }}>
          {foto ? (
            <img src={foto} alt="Vista previa" className="h-full w-full object-cover" data-testid="camara-preview" />
          ) : sinCamara ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center" data-testid="camara-simulada-aviso">
              <Camera size={40} className="text-[#2DD4BF]/60" />
              <p className="text-sm font-medium text-white">Sin cámara disponible</p>
              <p className="text-xs text-gray-500">Se genera una foto simulada con fecha y hora</p>
            </div>
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="camara-video" />
          )}
          {!foto && cuenta > 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/35">
              <motion.span
                key={cuenta}
                initial={{ scale: 1.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-8xl font-bold tabular-nums text-white drop-shadow-lg"
                data-testid="camara-cuenta"
              >
                {cuenta}
              </motion.span>
            </div>
          )}
        </div>

        {foto && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              onClick={repetir}
              className="flex h-13 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#1B212B] py-3.5 text-sm font-semibold text-white transition-all hover:border-[#2DD4BF]/50 active:scale-[0.98]"
              data-testid="camara-repetir"
            >
              <RefreshCcw size={17} /> Repetir
            </button>
            <button
              onClick={() => onCaptura(foto)}
              className="flex items-center justify-center gap-2 rounded-2xl bg-[#2DD4BF] py-3.5 text-sm font-bold text-black transition-all hover:brightness-110 active:scale-[0.98]"
              data-testid="camara-confirmar"
            >
              <Check size={18} /> {soloPrueba ? "Listo, se ve bien" : "Confirmar"}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
